import React, { useEffect, useState } from 'react';
import { ref, get, database } from '../firebase';
import { useNavigate } from 'react-router-dom';

const Playlist = () => {
  const [playlists, setPlaylists] = useState({});
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [loading, setLoading] = useState(true);
  const currentUser = JSON.parse(localStorage.getItem('currentUser'));
  const navigate = useNavigate();

  useEffect(() => {
    if (!currentUser) return;
    const sanitizedMobile = currentUser.mobile.replace(/[^0-9]/g, '_');
    const playlistRef = ref(database, `videoplayer/playlist/${sanitizedMobile}`);
    get(playlistRef).then(snapshot => {
      setPlaylists(snapshot.exists() ? snapshot.val() : {});
      setLoading(false);
    });
  }, [currentUser]);

  if (!currentUser) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">Access Denied</h2>
          <p className="text-gray-600 mb-6">Please log in to view your playlists.</p>
          <button 
            onClick={() => navigate('/login')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Loading your playlists...</p>
        </div>
      </div>
    );
  }

  if (Object.keys(playlists).length === 0) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full text-center">
          <h2 className="text-2xl font-bold text-gray-800 mb-4">No Playlists Found</h2>
          <p className="text-gray-600 mb-6">You haven't created any playlists yet.</p>
          <button 
            onClick={() => navigate('/home')}
            className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Browse Videos
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-10">
          <h1 className="text-3xl font-extrabold text-gray-900 sm:text-4xl">
            My Playlists
          </h1>
          <p className="mt-3 text-xl text-gray-500">
            {currentUser.name}'s video collections
          </p>
        </div>

        <div className="mb-10">
          <h2 className="text-xl font-semibold text-gray-800 mb-4">Select a Playlist</h2>
          <div className="flex flex-wrap gap-4">
            {Object.keys(playlists).map(name => {
              const hasVideo = playlists[name] && playlists[name].videoURL;
              return (
                <div 
                  key={name}
                  className="relative rounded-xl overflow-hidden w-full sm:w-64 h-24 transition-all duration-300 hover:scale-105 cursor-pointer shadow-lg"
                  onClick={() => setSelectedPlaylist(name)}
                >
                  {/* Blurred background */}
                  {hasVideo && (
                    <div className="absolute inset-0 z-0">
                      <img 
                        src={playlists[name].thumbnail} 
                        alt={playlists[name].title} 
                        className="w-full h-full object-cover filter blur-md brightness-50"
                      />
                    </div>
                  )}
                  
                  {/* Content overlay */}
                  <div className={`absolute inset-0 z-10 flex items-center justify-center p-4 rounded-xl border-2 ${
                    selectedPlaylist === name 
                      ? 'border-blue-400 bg-blue-500/20' 
                      : 'border-white/30 hover:border-blue-300/50'
                  } transition-all duration-300`}>
                    <div className="text-center w-full">
                      <h3 className={`font-bold truncate ${
                        hasVideo ? 'text-white drop-shadow-md' : 'text-gray-800'
                      }`}>
                        {name}
                      </h3>
                      <p className={`text-sm mt-1 ${
                        hasVideo ? 'text-blue-100' : 'text-gray-500'
                      }`}>
                        {hasVideo ? '1 video' : 'Empty playlist'}
                      </p>
                    </div>
                  </div>
                  
                  {/* Selected indicator */}
                  {selectedPlaylist === name && (
                    <div className="absolute top-2 right-2 z-20">
                      <div className="w-6 h-6 bg-blue-500 rounded-full flex items-center justify-center">
                        <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {selectedPlaylist && (
          <div className="bg-white rounded-xl shadow-md overflow-hidden transition-all duration-300">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <div>
                  <h3 className="text-2xl font-bold text-gray-800">
                    {selectedPlaylist}
                  </h3>
                  <p className="text-gray-500">
                    {playlists[selectedPlaylist]?.videoURL ? '1 video' : 'Empty playlist'}
                  </p>
                </div>
                <span className="px-3 py-1 bg-blue-100 text-blue-800 text-sm font-medium rounded-full">
                  {playlists[selectedPlaylist]?.videoURL ? 'Active' : 'Empty'}
                </span>
              </div>

              {playlists[selectedPlaylist]?.videoURL ? (
                <div className="group relative bg-gray-100 rounded-lg overflow-hidden transition-all duration-300 hover:shadow-xl">
                  <div className="aspect-w-16 aspect-h-9">
                    <img 
                      src={playlists[selectedPlaylist].thumbnail} 
                      alt={playlists[selectedPlaylist].title} 
                      className="w-full h-60 object-cover"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-20 group-hover:bg-opacity-30 transition-all duration-300 flex items-center justify-center">
                      <button 
                        onClick={() => navigate(`/video/${playlists[selectedPlaylist].id}`)}
                        className="w-16 h-16 bg-blue-600 bg-opacity-90 rounded-full flex items-center justify-center transform group-hover:scale-110 transition-transform duration-300"
                      >
                        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="text-lg font-bold text-gray-900 mb-2">
                      {playlists[selectedPlaylist].title}
                    </h4>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-gray-500">
                        Added on {new Date(playlists[selectedPlaylist].timestamp).toLocaleDateString()}
                      </span>
                      <button
                        onClick={() => navigate(`/video/${playlists[selectedPlaylist].id}`)}
                        className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 transition-colors flex items-center"
                      >
                        <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M14.752 11.168l-3.197-2.132A1 1 0 0010 9.87v4.263a1 1 0 001.555.832l3.197-2.132a1 1 0 000-1.664z" />
                        </svg>
                        Watch Now
                      </button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-12 border-2 border-dashed border-gray-200 rounded-lg">
                  <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M15 10l4.553-2.276A1 1 0 0121 8.618v6.764a1 1 0 01-1.447.894L15 14M5 18h8a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v8a2 2 0 002 2z" />
                  </svg>
                  <h3 className="mt-2 text-lg font-medium text-gray-900">No videos in this playlist</h3>
                  <p className="mt-1 text-gray-500">Add videos to enjoy your collection</p>
                  <div className="mt-6">
                    <button
                      onClick={() => navigate('/')}
                      className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700 focus:outline-none"
                    >
                      <svg className="-ml-1 mr-2 h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 6v6m0 0v6m0-6h6m-6 0H6" />
                      </svg>
                      Browse Videos
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default Playlist;